import { CalendarPlus, Globe, Heart, MagnifyingGlass, MapPin, NavigationArrow, Phone, X } from '@phosphor-icons/react';
import type { EventCategory } from '@sirocco/shared';
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { events, likesDesLieux, push } from '../../api/endpoints';
import { useTabNav } from '../../components/layout/TabNav';
import { useSession } from '../../store/SessionContext';
import { itineraires } from '../../utils/adresses';
import { distanceKm } from '../../utils/distance';
import { ouvrirPageExterne } from '../../utils/pageExterne';
import type { Lieu } from '../events/carte/moteur';
import type { LieuPropose } from '../events/CreateEventScreen';
import type { LikesDuLieu } from '../../firebase/lieux';
import { leverLaPastille } from './pastille';
import { t } from '../../i18n';
import { CarteDesLieux, couleurDe, dansLesLimites, type PoigneeDesLieux } from './CarteDesLieux';
import { FAMILLES, lieuxConnus, normaliser, SANS_GENRE, type FamilleDeLieu, type LieuTrouve } from './recherche';

/**
 * L'onglet Lieux : la ville vue du ciel, et ce qu'on peut y faire.
 *
 * On s'y promene comme dans Localiser. Chaque restaurant, stade ou musee
 * connu d'Apple y est une punaise, celle des evenements ; la toucher ouvre sa
 * fiche sans quitter la carte. Rien de tout cela n'est dans notre base.
 */

const ESSAIS_POSITION = 3;
const ENTRE_DEUX_ESSAIS_MS = 2500;
const DEPART_FICHE_MS = 420;

/*
  La ville et ses alentours : une quinzaine de kilometres de cote.

  Les limites du serveur disent son pays, ce qui ne veut rien dire pour trouver
  un restaurant. Les degres de longitude retrecissent en montant vers le nord.
*/
const RAYON_DE_LA_ZONE = 0.07;
function zoneAutourDe(centre: Lieu | undefined) {
  if (!centre) return undefined;
  const largeur = RAYON_DE_LA_ZONE / Math.cos((centre.lat * Math.PI) / 180);
  return {
    nord: centre.lat + RAYON_DE_LA_ZONE,
    sud: centre.lat - RAYON_DE_LA_ZONE,
    est: centre.lon + largeur,
    ouest: centre.lon - largeur,
  };
}

/*
  L'evenement cree depuis un lieu demarre sur la categorie qui lui ressemble. La nature
  et les commerces n'en ont pas de franche : on reste sur celle du formulaire.
*/
const CATEGORIE_DE_FAMILLE: Partial<Record<FamilleDeLieu, EventCategory>> = {
  restos: 'food',
  sorties: 'culture',
  sport: 'sport',
  culture: 'culture',
};

/*
  Les likes tiennent a l'identifiant d'Apple, le meme dans toutes les langues (mesure le
  1er novembre 2026). Un lieu rendu sans lui n'a que son nom traduit : pas de like.
*/
const IDENTIFIANT_D_APPLE = /^[A-Za-z0-9_-]{1,64}$/;

/* Les types qu'Apple connait, sous le nom qu'on leur donne a l'ecran. */
const TYPES = Object.entries(t.lieux.categories).map(([categorie, nom]) => ({
  categorie,
  nom,
  cle: normaliser(nom),
}));
const TYPES_PROPOSES = 8;

/* Le debut du nom ; plus loin dedans, seulement a partir de trois lettres. */
function typesPour(texte: string) {
  const mot = normaliser(texte.trim());
  if (!mot) return [];
  return TYPES.filter(({ cle }) => cle.startsWith(mot) || (mot.length >= 3 && cle.includes(mot))).slice(0, TYPES_PROPOSES);
}

/*
  Le type que veut dire ce qu'on a ecrit : son nom, au pluriel pres (« stades »), ou un
  debut assez long pour n'en designer qu'un (« foot »). Sinon, c'est un simple mot.

  On cherche alors son nom entier : chez Apple, « foot » ne rend que Foot Locker quand
  « football » rend les clubs et les terrains (mesure a Poissy le 1er novembre 2026).
*/
function typeDe(texte: string) {
  const mot = normaliser(texte.trim());
  const exact = TYPES.find(({ cle }) => cle === mot || cle === mot.replace(/s$/, ''));
  if (exact) return exact;
  const debuts = mot.length >= 4 ? TYPES.filter(({ cle }) => cle.startsWith(mot)) : [];
  return debuts.length === 1 ? debuts[0] : undefined;
}

/* Le nom du site, sans le reste de l'adresse : on doit le lire d'un coup d'oeil. */
function nomDuSite(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/* Un numero francais se lit par paires. Les autres restent tels qu'Apple les donne. */
function enNumero(telephone: string): string {
  const francais = /^\+33(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(telephone);
  return francais ? `0${francais.slice(1).join(' ')}` : telephone;
}

function enDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km`;
  return `${Math.round(km)} km`;
}

export function LieuxScreen() {
  const { server } = useSession();
  const navigate = useNavigate();
  const emplacement = useLocation();
  const { drag } = useTabNav();
  /*
    Rien ne se charge tant qu'on n'y est pas. L'ecran voisin est monte des que
    le doigt commence a balayer, et ce n'est pas une raison pour demander la
    position du membre ni pour payer des recherches chez Apple.
  */
  const [arrive, setArrive] = useState(!drag);
  useEffect(() => {
    if (!drag) setArrive(true);
  }, [drag]);

  const [lieux, setLieux] = useState<LieuTrouve[]>(lieuxConnus);
  /* Aucune choisie : on voit tout. Plusieurs peuvent l'etre a la fois. */
  const [familles, setFamilles] = useState<readonly FamilleDeLieu[]>([]);
  const [moi, setMoi] = useState<Lieu | null | undefined>(undefined);
  const [choisi, setChoisi] = useState<LieuTrouve | null>(null);
  const [sortant, setSortant] = useState<LieuTrouve | null>(null);
  const [recherche, setRecherche] = useState(false);
  const [tropLoin, setTropLoin] = useState(false);
  const [panne, setPanne] = useState(false);
  const poignee = useRef<PoigneeDesLieux | null>(null);
  const zone = useRef<HTMLDivElement>(null);
  const ficheRef = useRef<HTMLDivElement>(null);
  const derniereFiche = useRef<LieuTrouve | null>(null);
  const [likes, setLikes] = useState<LikesDuLieu | null>(null);
  const [texte, setTexte] = useState('');
  /* Ce qu'on a cherche et ce que ca a donne. Rien de cherche : la carte habituelle. */
  const [cherche, setCherche] = useState<string | null>(null);
  const [resultats, setResultats] = useState<LieuTrouve[] | null>(null);
  const [enQuete, setEnQuete] = useState(false);
  /* Comme dans le fil : un rond, et la barre seulement quand on le touche. */
  const [barre, setBarre] = useState(false);
  const champ = useRef<HTMLInputElement>(null);
  const derniereQuete = useRef(0);

  useEffect(() => {
    if (!arrive) return;
    let vivant = true;
    void (async () => {
      for (let essai = 0; essai < ESSAIS_POSITION && vivant; essai += 1) {
        const point = await events.whereAmI().catch(() => null);
        if (!vivant) return;
        if (point) return setMoi(point);
        const etat = await events.positionState().catch(() => 'refuse' as const);
        if (etat === 'refuse') break;
        await new Promise((suite) => window.setTimeout(suite, ENTRE_DEUX_ESSAIS_MS));
      }
      if (vivant) setMoi(null);
    })();
    return () => {
      vivant = false;
    };
  }, [arrive]);

  /* L'epingle levee suit la fiche, d'ou qu'on ait choisi. */
  useEffect(() => {
    leverLaPastille(choisi?.id ?? null);
  }, [choisi]);

  /* La fiche amene son point juste au-dessus d'elle. */
  useEffect(() => {
    if (!choisi) return;
    const cadre = zone.current?.getBoundingClientRect().height ?? 0;
    const fiche = ficheRef.current?.getBoundingClientRect().height ?? 0;
    const part = cadre > 0 ? Math.min(fiche / cadre, 0.6) : 0;
    poignee.current?.viser(choisi, part);
  }, [choisi]);

  /* Elle redescend par ou elle est venue : voir la meme chose sur les evenements. */
  useLayoutEffect(() => {
    if (choisi) {
      derniereFiche.current = choisi;
      setSortant(null);
      return;
    }
    const partie = derniereFiche.current;
    derniereFiche.current = null;
    if (!partie) return;
    setSortant(partie);
    const minuteur = window.setTimeout(() => setSortant(null), DEPART_FICHE_MS);
    return () => window.clearTimeout(minuteur);
  }, [choisi]);

  const zoneDeLaVille = zoneAutourDe(server?.centre);
  const fiche = choisi ?? sortant;
  const km = fiche && moi ? distanceKm(moi, fiche) : null;
  /* Tant qu'on ecrit, les types qui repondent prennent la place des familles. */
  const proposes = texte.trim() !== cherche ? typesPour(texte) : [];
  const lieuAimable = fiche && IDENTIFIANT_D_APPLE.test(fiche.id) ? fiche.id : null;

  useEffect(() => {
    setLikes(null);
    if (!lieuAimable) return;
    return likesDesLieux.ecouter(lieuAimable, setLikes);
  }, [lieuAimable]);

  function oublierLaRecherche() {
    derniereQuete.current += 1;
    setTexte('');
    setCherche(null);
    setResultats(null);
    setEnQuete(false);
  }

  function ouvrirLaBarre() {
    setBarre(true);
    /* Le clavier monte avec la barre, pas apres. */
    requestAnimationFrame(() => champ.current?.focus());
  }

  function fermerLaBarre() {
    oublierLaRecherche();
    setBarre(false);
    champ.current?.blur();
  }

  async function lancerLaRecherche(mots: string, categorie: string | null) {
    const quete = (derniereQuete.current += 1);
    champ.current?.blur();
    setTexte(mots);
    setCherche(mots);
    setChoisi(null);
    setEnQuete(true);
    const trouves = (await poignee.current?.chercher(mots, categorie)) ?? [];
    /* Une recherche plus recente est partie, ou l'on a tout efface. */
    if (quete !== derniereQuete.current) return;
    setEnQuete(false);
    setResultats(trouves);
    /* Un seul : autant l'ouvrir, la fiche l'amene au-dessus d'elle. */
    if (trouves.length === 1) setChoisi(trouves[0]!);
    else poignee.current?.montrer(trouves);
  }

  function surEnvoi(evenement: FormEvent) {
    evenement.preventDefault();
    const mots = texte.trim();
    if (!mots) return oublierLaRecherche();
    const type = typeDe(mots);
    void lancerLaRecherche(type?.nom ?? mots, type?.categorie ?? null);
  }

  function creerIci(lieu: LieuTrouve) {
    const propose: LieuPropose = {
      libelle: lieu.adresse ? `${lieu.nom}, ${lieu.adresse}` : lieu.nom,
      lat: lieu.lat,
      lon: lieu.lon,
      categorie: CATEGORIE_DE_FAMILLE[lieu.famille],
    };
    /* Par-dessus la carte : y revenir la retrouve telle quelle, fiche ouverte. */
    navigate('/app/events/new', { state: { lieu: propose, fond: emplacement } });
  }

  return (
    <div className="ev-carte-ecran lx" data-sans-balayage>
      <div className="ev-carte" ref={zone}>
        {arrive && (
          <CarteDesLieux
            lieux={resultats ?? lieux}
            familles={familles}
            resultat={resultats !== null}
            moi={moi}
            centre={server?.centre ?? null}
            limites={zoneDeLaVille}
            poignee={poignee}
            choisi={choisi}
            onChoisir={setChoisi}
            onGeste={() => {
              setChoisi(null);
              champ.current?.blur();
            }}
            onTrouves={() => setLieux(lieuxConnus())}
            onRecherche={setRecherche}
            onTropLoin={setTropLoin}
            onPanne={() => setPanne(true)}
          />
        )}

        <span className="lx-sonde-bas" aria-hidden="true" />
        {/* La meme tete que le fil : son titre, et la loupe qui s'ouvre en barre. */}
        <div className={`feed__head lx-tete${barre ? ' feed__head--open' : ''}`}>
          <h1 className="feed__title lx-titre">
            {server?.shortName ?? server?.name}
          </h1>
          <div className="feed__zone">
            <form className="feed__search" role="search" onSubmit={surEnvoi}>
              <input
                ref={champ}
                type="search"
                className="feed__search-input"
                enterKeyHint="search"
                autoCorrect="off"
                autoCapitalize="off"
                placeholder={t.lieux.exemple}
                aria-label={t.lieux.chercher}
                aria-hidden={!barre}
                tabIndex={barre ? 0 : -1}
                value={texte}
                onChange={(evenement) => {
                  if (!evenement.target.value.trim()) oublierLaRecherche();
                  else setTexte(evenement.target.value);
                }}
              />
              {barre && (
                <button
                  type="button"
                  className="feed__search-clear"
                  aria-label={t.lieux.effacer}
                  onMouseDown={(evenement) => evenement.preventDefault()}
                  onClick={fermerLaBarre}
                >
                  <X size={15} weight="bold" />
                </button>
              )}
            </form>
            <button
              type="button"
              className="feed__search-btn lx-loupe"
              aria-label={t.lieux.chercher}
              aria-expanded={barre}
              tabIndex={barre ? -1 : 0}
              onClick={ouvrirLaBarre}
            >
              <MagnifyingGlass size={19} weight="bold" />
            </button>
          </div>
        </div>

        {proposes.length > 0 ? (
          <div className="lx-filtres" role="group" aria-label={t.lieux.chercher}>
            {proposes.map((type) => (
              <button
                key={type.categorie}
                type="button"
                className="lx-filtre"
                onClick={() => void lancerLaRecherche(type.nom, type.categorie)}
              >
                {type.nom}
              </button>
            ))}
          </div>
        ) : (
          <div className="lx-filtres" role="group" aria-label={t.lieux.filtrer}>
            <button
              type="button"
              className={`lx-filtre${familles.length === 0 && cherche === null ? ' lx-filtre--actif' : ''}`}
              aria-pressed={familles.length === 0 && cherche === null}
              onClick={() => {
                setChoisi(null);
                fermerLaBarre();
                setFamilles([]);
              }}
            >
              {t.lieux.tout}
            </button>
            {FAMILLES.map((une) => (
              <button
                key={une.id}
                type="button"
                className={`lx-filtre${cherche === null && familles.includes(une.id) ? ' lx-filtre--actif' : ''}`}
                aria-pressed={cherche === null && familles.includes(une.id)}
                onClick={() => {
                  setChoisi(null);
                  fermerLaBarre();
                  setFamilles((avant) =>
                    avant.includes(une.id) ? avant.filter((autre) => autre !== une.id) : [...avant, une.id],
                  );
                }}
              >
                <span className="lx-filtre__point" style={{ background: une.couleur }} aria-hidden="true" />
                {t.lieux.familles[une.id]}
              </button>
            ))}
          </div>
        )}

        <div className="lx-bandeaux">
          {panne ? (
            <p className="ev-carte__bandeau">{t.carte.apple.pasOuverte}</p>
          ) : enQuete ? (
            <p className="ev-carte__bandeau ev-carte__bandeau--discret">{t.lieux.recherche}</p>
          ) : resultats?.length === 0 ? (
            <p className="ev-carte__bandeau">{t.lieux.rienTrouve}</p>
          ) : resultats ? null : tropLoin ? (
            <p className="ev-carte__bandeau">{t.lieux.rapproche}</p>
          ) : recherche && lieux.length === 0 ? (
            <p className="ev-carte__bandeau ev-carte__bandeau--discret">{t.lieux.recherche}</p>
          ) : null}
          {moi === null && (
            <button
              type="button"
              className="ev-carte__bandeau"
              onClick={() => void push.ouvrirLesReglagesDuTelephone()}
            >
              {t.carte.ecran.activerLocalisation}
            </button>
          )}
        </div>

        {/* Plus bas qu'elle, il depasserait de sous la fiche. */}
        {moi && !fiche && dansLesLimites(moi, zoneDeLaVille) && (
          <button
            type="button"
            className="ev-carte__recentrer lx-recentrer"
            aria-label={t.carte.ecran.revenir}
            onClick={() => poignee.current?.recentrer()}
          >
            <NavigationArrow size={20} weight="fill" />
          </button>
        )}

        {fiche && (
          <div
            className={`ev-fiche lx-fiche${choisi ? '' : ' ev-fiche--part'}`}
            role="dialog"
            aria-label={fiche.nom}
            ref={ficheRef}
          >
            <button
              type="button"
              className="lx-fiche__fermer"
              aria-label={t.lieux.fermer}
              onClick={() => setChoisi(null)}
            >
              <X size={18} />
            </button>
            <h2 className="lx-fiche__nom">{fiche.nom}</h2>
            <p className="lx-fiche__genre">
              <span className="lx-filtre__point" style={{ background: couleurDe(fiche) }} aria-hidden="true" />
              {[
                fiche.categorie === SANS_GENRE
                  ? null
                  : (fiche.categorie && t.lieux.categories[fiche.categorie as keyof typeof t.lieux.categories]) ||
                    t.lieux.familles[fiche.famille],
                km === null ? null : enDistance(km),
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <div className="lx-fiche__actions">
              {lieuAimable && (
                <button
                  type="button"
                  className={`lx-fiche__like${likes?.moi ? ' lx-fiche__like--aime' : ''}`}
                  aria-pressed={likes?.moi ?? false}
                  aria-label={likes?.moi ? t.lieux.nePlusAimer : t.lieux.aimer}
                  disabled={!likes}
                  onClick={() => {
                    if (likes) void likesDesLieux.poser(lieuAimable, !likes.moi).catch(() => undefined);
                  }}
                >
                  <Heart size={16} weight={likes?.moi ? 'fill' : 'regular'} aria-hidden="true" />
                  {likes && likes.total > 0 && likes.total}
                </button>
              )}
              <button type="button" className="lx-fiche__creer" onClick={() => creerIci(fiche)}>
                <CalendarPlus size={16} aria-hidden="true" />
                {t.lieux.creerUnEvenement}
              </button>
            </div>
            {/*
              Tout ce qu'Apple sait, ecrit en clair. Il connait l'adresse de
              presque tout, mais le site d'un lieu sur deux : sans site, on
              propose de chercher le nom, plutot que de ne rien dire.
            */}
            <ul className="lx-fiche__infos">
              {fiche.adresse && (
                <li className="lx-fiche__info">
                  <MapPin size={18} aria-hidden="true" />
                  <span>{fiche.adresse}</span>
                </li>
              )}
              {fiche.telephone && (
                <li>
                  <a
                    className="lx-fiche__info lx-fiche__info--lien"
                    href={`tel:${fiche.telephone}`}
                    aria-label={`${t.lieux.appeler} ${enNumero(fiche.telephone)}`}
                  >
                    <Phone size={18} aria-hidden="true" />
                    <span>{enNumero(fiche.telephone)}</span>
                  </a>
                </li>
              )}
              <li>
                <button
                  type="button"
                  className="lx-fiche__info lx-fiche__info--lien"
                  onClick={() =>
                    void ouvrirPageExterne(
                      fiche.site ??
                        `https://www.google.com/search?q=${encodeURIComponent(`${fiche.nom} ${fiche.adresse}`)}`,
                    )
                  }
                >
                  {fiche.site ? <Globe size={18} aria-hidden="true" /> : <MagnifyingGlass size={18} aria-hidden="true" />}
                  <span>{fiche.site ? nomDuSite(fiche.site) : t.lieux.chercherSurLeWeb}</span>
                </button>
              </li>
            </ul>
            <ul className="ev-lieu__routes lx-fiche__routes">
              {itineraires(fiche.adresse || fiche.nom, fiche.lat, fiche.lon).map((route) => (
                <li key={route.nom}>
                  <a className="ev-lieu__route" href={route.lien} target="_blank" rel="noreferrer">
                    <img className="ev-lieu__logo" src={route.logo} alt="" />
                    {route.nom}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
